// Load-screen GATES for async assets (2026-10-23, the realistic-models pass).
//
// A licensed glTF creature (shark, kraken) arrives over the network while the
// match load screen is up. Swapping it in AFTER the screen drops would be a
// mid-fight pop-in plus a cold shader compile, so the swap is only allowed
// inside the load-screen window:
//
//   - the builder registers its load+swap promise with holdLoadScreen() and
//     keeps the returned ticket;
//   - GameRenderer.precompile() calls awaitLoadGates() BEFORE its scene-wide
//     shader compile, so a swap that lands in time is in the scene when the
//     programs are built (covered by the real instance, not a warm proxy);
//   - when that wait ends (all settled, or the deadline) the epoch advances and
//     every older ticket is CLOSED: a late load leaves the procedural model up
//     for the rest of the round (the cache serves the next map load). Same
//     "never a mid-match swap" rule as the photo surfaces in pbrTextures.ts.
//
// No DOM, no three — just promise bookkeeping.

let pending: Promise<unknown>[] = [];
let epoch = 0;

/** Register an in-flight load (+ swap). Returns the ticket to test with
 *  loadScreenOpen() before mutating anything the player could see. */
export function holdLoadScreen(p: Promise<unknown>): number {
  pending.push(p.catch(() => undefined));
  return epoch;
}

/** True while the load-screen window this ticket was issued in is still open. */
export function loadScreenOpen(ticket: number): boolean {
  return ticket === epoch;
}

/** Wait (bounded) for every registered gate, then close the window. Called by
 *  GameRenderer.precompile() under the load screen. Never throws. */
export async function awaitLoadGates(maxMs: number): Promise<void> {
  const list = pending;
  pending = [];
  try {
    if (list.length && maxMs > 0) {
      let timer = 0;
      await Promise.race([
        Promise.allSettled(list),
        new Promise<void>((resolve) => { timer = window.setTimeout(resolve, maxMs); }),
      ]);
      window.clearTimeout(timer);
    }
  } catch { /* best-effort */ }
  epoch++;
}

// WARM ROOTS. A dormant spare (match.ts prebuildMapBeasts) is HIDDEN, and
// renderer.compile() only walks VISIBLE objects — so a glTF creature mounted
// on a spare would still cold-compile its skinned programs (lit + the skinned
// shadow-depth variant) the first time it appears mid-round. Spares register
// their root here; precompile() shows them for one compile + one shadow-casting
// render under the load screen, then restores their visibility.
const warmRoots = new Set<{ visible: boolean }>();
export function registerWarmRoot(o: { visible: boolean }): void { warmRoots.add(o); }
export function unregisterWarmRoot(o: { visible: boolean }): void { warmRoots.delete(o); }
/** Show every registered root; returns a restore callback. */
export function revealWarmRoots(): () => void {
  const prev: [{ visible: boolean }, boolean][] = [];
  for (const o of warmRoots) { prev.push([o, o.visible]); o.visible = true; }
  return () => { for (const [o, v] of prev) o.visible = v; };
}
export const warmRootCount = (): number => warmRoots.size;
